import { AstroCareersViewTransitions } from "@components/AstroViewTransitions"
import CareersCard from "@components/Careers/CareersCard"
import People from "@components/Careers/People"
import { useRouteShellContext } from "@components/routeShellContext"
import { BigHeading, SiteShell } from "@components/SiteShell"
import { careersListPageQueryOptions } from "@data/publicPageDataQuery"
import { careersPoints } from "@lib/data"
import * as m from "@paraglide/messages"
import type { CareersListPageData } from "@site-types/publicPageData"
import { createFileRoute } from "@tanstack/react-router"
import { localizedRouteHead } from "@utils/routeHead"
import { getLocalizedCareers } from "@utils/siteData"

export const Route = createFileRoute("/$locale/careers/")({
  loader: ({ context, params }) =>
    context.queryClient.ensureQueryData(careersListPageQueryOptions(params.locale as "en" | "ja")),
  head: ({ params }) => {
    const locale = params.locale as "en" | "ja"
    return localizedRouteHead({ locale, page: "careers", path: `/${locale}/careers` })
  },
  component: RouteComponent,
})

function RouteComponent() {
  const data = Route.useLoaderData()
  return (
    <SiteShell>
      <CareersListPage data={data} />
    </SiteShell>
  )
}

export function CareersListPage({ data }: { data: CareersListPageData }) {
  const { locale } = useRouteShellContext()
  const careers = getLocalizedCareers(data, locale)
  const { whatWeDo, whyWeDo, gameCompanies, uniqueWorkplace } = careersPoints(locale)
  return (
    <>
      <AstroCareersViewTransitions careers={careers} />
      <BigHeading>{(m.careers_heading_label({}, { locale: locale }) || "Join Us").toUpperCase()}</BigHeading>
      <div className="relative z-[5]">
        <div
          data-id="careers-people-panel"
          className="relative z-[2] m-[-20px_30px_30px] w-[calc(100%_-_60px)] max-w-full before:absolute before:inset-0 before:z-0 before:bg-[var(--pink)] before:content-[''] max-careers:mx-0 max-careers:w-full before:[--clip-inset:40px] before:[clip-path:polygon(var(--clip-inset)_0%,100%_0%,100%_calc(100%_-_var(--clip-inset)),calc(100%_-_var(--clip-inset))_100%,0%_100%,0%_var(--clip-inset))]"
        >
          <People />
          <h1 className="relative z-[2] flex flex-wrap justify-center gap-x-[0.18em] px-[30px] pt-[40px] text-center text-[5rem] leading-[1em] [&_i]:text-[var(--deep-blue)]">
            <span>{m.careers_titleStart({}, { locale: locale }).toUpperCase()}</span>{" "}
            <i
              className="fa-regular fa-up"
              aria-hidden="true"
            />{" "}
            <span>{m.careers_titleEnd({}, { locale: locale }).toUpperCase()}</span>
          </h1>
          <div className="relative z-[2] mx-auto grid max-w-[var(--max-width-column)] grid-cols-3 gap-[30px] p-[5vw] text-[var(--white)] max-sm:grid-cols-1 max-careers-wide:grid-cols-2 max-careers-wide:py-[10vw] max-sm:[&_[data-careers-column=unique]]:col-[1/span_1] max-careers-wide:[&_[data-careers-column=unique]]:col-[1/span_2] [&_h3]:text-[3em] [&_h3]:text-[var(--white)] max-md:[&_h3]:text-[2em] [&_li]:ml-[30px] [&_li]:list-outside [&_li]:list-[circle] [&_li]:text-[1.6rem] [&_li]:text-[var(--white)] [&_p]:text-[1.6rem] [&_p]:text-[var(--white)] [&_ul]:text-[1.6rem]">
            <div>
              <h3>{m.careers_whatWeDo_label({}, { locale: locale }).toUpperCase()}</h3>
              <p>{locale !== "ja" ? m.careers_whatWeDo_subheading({}, { locale: locale }) : ""}</p>
              <ul>{whatWeDo.map((item) => item && <li key={item}>{item}</li>)}</ul>
            </div>
            <div>
              <h3>{m.careers_whyWeDo_label({}, { locale: locale }).toUpperCase()}</h3>
              <ul>{whyWeDo.map((item) => item && <li key={item}>{item}</li>)}</ul>
            </div>
            <div data-careers-column="unique">
              <h3>{m.careers_uniqueWorkplace_label({}, { locale: locale }).toUpperCase()}</h3>
              <ul>
                <li>
                  {m.careers_uniqueWorkplace_global({}, { locale: locale })}
                  <ul>
                    <li>{gameCompanies}</li>
                  </ul>
                </li>
                {uniqueWorkplace.map((item) => item && <li key={item}>{item}</li>)}
              </ul>
            </div>
          </div>
        </div>
        <div
          data-id="careers-positions"
          className="mx-auto mb-[30px] max-w-[1400px] px-[30px] [--position-bg-color:var(--white)]"
        >
          <ul className="grid grid-cols-4 gap-[20px] max-careers-wide:grid-cols-3 max-page:grid-cols-2">
            {careers.map((position, index) => (
              <CareersCard
                key={`${locale}-${position.slug?.current ?? "career"}-${index}`}
                position={position}
                priority={index === 0}
              />
            ))}
          </ul>
        </div>
      </div>
    </>
  )
}
